import React, { useState, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InfoIcon from '@mui/icons-material/Info';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Area,
} from 'recharts';
import { useOptimization } from '../../context/OptimizationContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`charts-tabpanel-${index}`}
      aria-labelledby={`charts-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ChartsView: React.FC = () => {
  const { results, convergenceData } = useOptimization();
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const chartColors = {
    ga: '#1976d2',
    culture: '#9c27b0',
    frog: '#ed6c02',
    sflaga: '#2e7d32',
    pso: '#d32f2f',
    gapso: '#0288d1',
  };

  // Prepare fitness comparison data
  const fitnessComparisonData = useMemo(() => {
    if (!results) return [];
    return [{
      name: 'Algorithms',
      GA: results.ga.fitness,
      Culture: results.culture.fitness,
      Frog: results.frog.fitness,
      SFLAGA: results.sflaga.fitness,
      PSO: results.pso.fitness,
      GAPSO: results.gapso.fitness,
    }];
  }, [results]);

  // Prepare convergence data (Scenario 1 = first run, Scenario 2 = second run)
  // For now, we'll use the same data for both scenarios
  const convergenceScenario1 = useMemo(() => {
    if (!convergenceData || convergenceData.ga.length === 0) return [];
    
    const maxLength = Math.max(
      convergenceData.ga.length,
      convergenceData.culture.length,
      convergenceData.frog.length,
      convergenceData.sflaga.length,
      convergenceData.pso.length,
      convergenceData.gapso.length
    );

    const data = [];
    for (let i = 0; i < maxLength; i++) {
      data.push({
        iteration: i + 1,
        GA: convergenceData.ga[i]?.fitness || 0,
        Culture: convergenceData.culture[i]?.fitness || 0,
        Frog: convergenceData.frog[i]?.fitness || 0,
        SFLAGA: convergenceData.sflaga[i]?.fitness || 0,
        PSO: convergenceData.pso[i]?.fitness || 0,
        GAPSO: convergenceData.gapso[i]?.fitness || 0,
      });
    }
    return data;
  }, [convergenceData]);

  // Similarity data (relative to SFLAGA)
  const similarityData = useMemo(() => {
    if (!results) return [];
    const sflagaFitness = results.sflaga.fitness;
    if (sflagaFitness === 0) return [];
    
    return [{
      name: 'Similarity',
      GA: results.ga.fitness / sflagaFitness,
      Culture: results.culture.fitness / sflagaFitness,
      Frog: results.frog.fitness / sflagaFitness,
      PSO: results.pso.fitness / sflagaFitness,
      GAPSO: results.gapso.fitness / sflagaFitness,
    }];
  }, [results]);

  // Energy consumption data
  const energyData = useMemo(() => {
    if (!results) return [];
    return [{
      name: 'Energy',
      GA: results.ga.energy,
      Culture: results.culture.energy,
      Frog: results.frog.energy,
      SFLAGA: results.sflaga.energy,
      PSO: results.pso.energy,
      GAPSO: results.gapso.energy,
    }];
  }, [results]);

  // Execution cost data
  const costData = useMemo(() => {
    if (!results) return [];
    return [{
      name: 'Cost',
      GA: results.ga.cost,
      Culture: results.culture.cost,
      Frog: results.frog.cost,
      SFLAGA: results.sflaga.cost,
      PSO: results.pso.cost,
      GAPSO: results.gapso.cost,
    }];
  }, [results]);

  // Response Time data
  const responseTimeData = useMemo(() => {
    if (!results) return [];
    return [{
      name: 'Response Time',
      GA: results.ga.responseTime,
      Culture: results.culture.responseTime,
      Frog: results.frog.responseTime,
      SFLAGA: results.sflaga.responseTime,
      PSO: results.pso.responseTime,
      GAPSO: results.gapso.responseTime,
    }];
  }, [results]);

  // Execution Time data
  const executionTimeData = useMemo(() => {
    if (!results) return [];
    return [{
      name: 'Execution Time',
      GA: results.ga.executionTime ?? 0,
      Culture: results.culture.executionTime ?? 0,
      Frog: results.frog.executionTime ?? 0,
      SFLAGA: results.sflaga.executionTime ?? 0,
      PSO: results.pso.executionTime ?? 0,
      GAPSO: results.gapso.executionTime ?? 0,
    }];
  }, [results]);

  // Normalized performance data (all metrics normalized to 0-1 scale)
  const normalizedData = useMemo(() => {
    if (!results) return [];
    
    const algorithms = ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const;
    const algorithmNames = ['GA', 'Culture', 'Frog', 'SFLAGA', 'PSO', 'GAPSO'];
    
    // Find min/max for normalization
    const fitnessValues = algorithms.map(key => results[key].fitness);
    const energyValues = algorithms.map(key => results[key].energy);
    const costValues = algorithms.map(key => results[key].cost);
    const responseTimeValues = algorithms.map(key => results[key].responseTime);
    
    const maxFitness = Math.max(...fitnessValues);
    const minFitness = Math.min(...fitnessValues);
    const maxEnergy = Math.max(...energyValues);
    const minEnergy = Math.min(...energyValues);
    const maxCost = Math.max(...costValues);
    const minCost = Math.min(...costValues);
    const maxResponseTime = Math.max(...responseTimeValues);
    const minResponseTime = Math.min(...responseTimeValues);
    
    const normalize = (value: number, min: number, max: number, reverse: boolean = false) => {
      if (max === min) return 0.5;
      const normalized = (value - min) / (max - min);
      return reverse ? 1 - normalized : normalized;
    };
    
    return algorithms.map((key, idx) => ({
      name: algorithmNames[idx],
      Fitness: normalize(results[key].fitness, minFitness, maxFitness, false), // Higher is better
      'Energy Efficiency': normalize(results[key].energy, minEnergy, maxEnergy, true), // Lower energy = higher efficiency
      'Cost Efficiency': normalize(results[key].cost, minCost, maxCost, true), // Lower cost = higher efficiency
      'Response Time Efficiency': normalize(results[key].responseTime, minResponseTime, maxResponseTime, true), // Lower RT = higher efficiency
    }));
  }, [results]);

  // Multi-metric summary data
  const multiMetricData = useMemo(() => {
    if (!results) return [];
    
    const algorithms = ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const;
    const algorithmNames = ['GA', 'Culture', 'Frog', 'SFLAGA', 'PSO', 'GAPSO'];
    
    return algorithms.map((key, idx) => ({
      name: algorithmNames[idx],
      Fitness: results[key].fitness,
      Energy: results[key].energy,
      Cost: results[key].cost,
      ResponseTime: results[key].responseTime,
    }));
  }, [results]);

  // Convergence speed analysis - calculate improvement rate
  const convergenceSpeedData = useMemo(() => {
    if (!convergenceData || convergenceData.ga.length === 0) return [];
    
    const algorithms = ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'] as const;
    const algorithmNames = ['GA', 'Culture', 'Frog', 'SFLAGA', 'PSO', 'GAPSO'];
    
    return algorithms.map((key, idx) => {
      const data = convergenceData[key];
      if (data.length < 2) return { name: algorithmNames[idx], improvementRate: 0, iterationsTo90Percent: 0 };
      
      const initialFitness = data[0].fitness;
      const finalFitness = data[data.length - 1].fitness;
      const improvementRate = finalFitness > initialFitness 
        ? (finalFitness - initialFitness) / data.length 
        : 0;
      
      // Find iteration where 90% of final improvement is reached
      const targetFitness = initialFitness + (finalFitness - initialFitness) * 0.9;
      let iterationsTo90Percent = data.length;
      for (let i = 0; i < data.length; i++) {
        if (data[i].fitness >= targetFitness) {
          iterationsTo90Percent = i + 1;
          break;
        }
      }
      
      return {
        name: algorithmNames[idx],
        improvementRate: improvementRate * 1000, // Scale for visibility
        iterationsTo90Percent,
      };
    });
  }, [convergenceData]);

  const hasData = results !== null && convergenceData !== null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ShowChartIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2">
          Charts & Visualizations
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* First Row of Tabs */}
          <Tabs 
            value={value < 5 ? value : false} 
            onChange={(e, newValue) => {
              if (newValue !== false) handleChange(e, newValue);
            }} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              minHeight: 48,
              '& .MuiTab-root': { 
                minWidth: { xs: 100, sm: 140 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none',
              }
            }}
          >
            <Tab label="Fitness Comparison" onClick={() => setValue(0)} />
            <Tab label="Convergence - S1" onClick={() => setValue(1)} />
            <Tab label="Convergence - S2" onClick={() => setValue(2)} />
            <Tab label="Similarity" onClick={() => setValue(3)} />
            <Tab label="Energy" onClick={() => setValue(4)} />
          </Tabs>
          
          {/* Second Row of Tabs */}
          <Tabs 
            value={value >= 5 && value <= 10 ? value - 5 : false} 
            onChange={(e, newValue) => {
              if (newValue !== false) handleChange(e, newValue + 5);
            }} 
            variant="scrollable" 
            scrollButtons="auto"
            sx={{ 
              minHeight: 48,
              '& .MuiTab-root': { 
                minWidth: { xs: 100, sm: 140 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textTransform: 'none',
              }
            }}
          >
            <Tab label="Cost" onClick={() => setValue(5)} />
            <Tab label="Response Time" onClick={() => setValue(6)} />
            <Tab label="Execution Time" onClick={() => setValue(7)} />
            <Tab label="Normalized" onClick={() => setValue(8)} />
            <Tab label="Multi-Metric" onClick={() => setValue(9)} />
            <Tab label="Convergence Speed" onClick={() => setValue(10)} />
          </Tabs>
        </Box>
      </Box>

      {/* Fitness Comparison Chart */}
      <TabPanel value={value} index={0}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Fitness Comparison Across Algorithms
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart displays the final fitness scores for all six optimization algorithms. 
              Fitness is calculated based on QoS attributes (Response Time, Availability, Throughput, etc.) 
              using a weighted aggregation formula. <strong>Higher fitness values indicate better solutions.</strong> 
              Use this chart to quickly compare which algorithm achieved the best overall performance.
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={fitnessComparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Convergence Scenario 1 */}
      <TabPanel value={value} index={1}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Convergence Analysis - Scenario 1
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This line chart shows how each algorithm's fitness improves over iterations (generations). 
              The X-axis represents iteration numbers, and the Y-axis shows fitness values. 
              <strong> Steeper upward curves and higher final values indicate better convergence performance.</strong> 
              Algorithms that converge quickly and reach higher fitness values are generally preferred. 
              Use this chart to understand the optimization behavior and convergence speed of each algorithm.
            </Typography>
          </Alert>
          {!hasData || convergenceScenario1.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={convergenceScenario1}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="iteration" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="GA" stroke={chartColors.ga} name="GA" dot={false} />
                <Line type="monotone" dataKey="Culture" stroke={chartColors.culture} name="Culture" dot={false} />
                <Line type="monotone" dataKey="Frog" stroke={chartColors.frog} name="Frog" dot={false} />
                <Line type="monotone" dataKey="SFLAGA" stroke={chartColors.sflaga} name="SFLAGA" dot={false} />
                <Line type="monotone" dataKey="PSO" stroke={chartColors.pso} name="PSO" dot={false} />
                <Line type="monotone" dataKey="GAPSO" stroke={chartColors.gapso} name="GAPSO" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Convergence Scenario 2 (same as Scenario 1 for now) */}
      <TabPanel value={value} index={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Convergence Analysis - Scenario 2
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This line chart displays the convergence behavior in a second scenario (currently using the same data as Scenario 1). 
              Comparing convergence patterns across scenarios helps identify algorithm stability and consistency. 
              <strong> Consistent performance across multiple runs indicates reliable algorithms.</strong> 
              This visualization is useful for analyzing how algorithms perform under different conditions or parameter settings.
            </Typography>
          </Alert>
          {!hasData || convergenceScenario1.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={convergenceScenario1}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="iteration" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="GA" stroke={chartColors.ga} name="GA" dot={false} />
                <Line type="monotone" dataKey="Culture" stroke={chartColors.culture} name="Culture" dot={false} />
                <Line type="monotone" dataKey="Frog" stroke={chartColors.frog} name="Frog" dot={false} />
                <Line type="monotone" dataKey="SFLAGA" stroke={chartColors.sflaga} name="SFLAGA" dot={false} />
                <Line type="monotone" dataKey="PSO" stroke={chartColors.pso} name="PSO" dot={false} />
                <Line type="monotone" dataKey="GAPSO" stroke={chartColors.gapso} name="GAPSO" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Similarity Chart */}
      <TabPanel value={value} index={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Algorithm Similarity (Relative to SFLAGA)
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart compares algorithm performance relative to SFLAGA (Shuffled Frog Leaping Algorithm - Genetic Algorithm hybrid). 
              Each bar shows the ratio of an algorithm's fitness to SFLAGA's fitness. 
              <strong> A value of 1.0 means equal performance, values above 1.0 indicate better performance than SFLAGA, 
              and values below 1.0 indicate worse performance.</strong> 
              This comparison helps identify which algorithms produce similar or superior results compared to the hybrid approach.
            </Typography>
          </Alert>
          {!hasData || similarityData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={similarityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Energy Consumption Chart */}
      <TabPanel value={value} index={4}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Energy Consumption Comparison (Joules)
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart displays the total energy consumption for each algorithm's solution, measured in Joules. 
              Energy is calculated using the formula: Energy = (α × ResponseTime + β × Latency) × Power, 
              where α=0.6, β=0.4, and Power=3.0W. 
              <strong> Lower energy consumption indicates more energy-efficient solutions,</strong> 
              which is crucial for cloud computing and resource-constrained environments. 
              This metric helps identify algorithms that optimize for energy efficiency.
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={energyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Execution Cost Chart */}
      <TabPanel value={value} index={5}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Execution Cost Comparison
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart shows the execution cost for each algorithm's solution. 
              Cost is calculated as the sum of response times across all tasks, multiplied by a cost coefficient (typically 1.0). 
              <strong> Lower execution costs represent more cost-effective solutions,</strong> 
              which is important for cloud service composition where computational resources are billed based on usage. 
              Use this chart to compare the economic efficiency of different optimization approaches.
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={costData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Response Time Chart */}
      <TabPanel value={value} index={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Response Time Comparison
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart displays the total response time for each algorithm's solution, measured in milliseconds. 
              Response time is a critical QoS metric that represents the time taken to execute all tasks in the service composition. 
              <strong> Lower response times indicate faster solutions,</strong> which are essential for real-time applications and 
              user experience. This metric helps identify algorithms that optimize for latency and system responsiveness.
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={responseTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Execution Time Chart */}
      <TabPanel value={value} index={7}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Execution Time Comparison
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This bar chart displays the execution time for each algorithm, measured in milliseconds. 
              Execution time represents the actual computational time required by each algorithm to complete the optimization process. 
              <strong> Lower execution times indicate faster algorithms,</strong> which is crucial for real-time applications and 
              when computation resources are limited. This metric helps identify algorithms that provide a good balance between 
              solution quality and computational efficiency.
            </Typography>
          </Alert>
          {!hasData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={executionTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Time (ms)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="GA" fill={chartColors.ga} />
                <Bar dataKey="Culture" fill={chartColors.culture} />
                <Bar dataKey="Frog" fill={chartColors.frog} />
                <Bar dataKey="SFLAGA" fill={chartColors.sflaga} />
                <Bar dataKey="PSO" fill={chartColors.pso} />
                <Bar dataKey="GAPSO" fill={chartColors.gapso} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Normalized Performance Chart */}
      <TabPanel value={value} index={8}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Normalized Performance Comparison
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This chart displays all performance metrics normalized to a 0-1 scale, enabling fair comparison across different 
              metric types. All metrics are scaled so that higher values (closer to 1.0) indicate better performance. 
              <strong> This normalization allows you to see which algorithm performs best overall across multiple dimensions.</strong> 
              Efficiency metrics (Energy, Cost, Response Time) are inverted so that lower values become higher scores. 
              This visualization is particularly useful for multi-objective optimization analysis.
            </Typography>
          </Alert>
          {!hasData || normalizedData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={normalizedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 1]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Fitness" fill={chartColors.ga} />
                <Bar dataKey="Energy Efficiency" fill={chartColors.culture} />
                <Bar dataKey="Cost Efficiency" fill={chartColors.frog} />
                <Bar dataKey="Response Time Efficiency" fill={chartColors.sflaga} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Multi-Metric Summary Chart */}
      <TabPanel value={value} index={9}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Multi-Metric Performance Summary
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This composed chart displays all key performance metrics (Fitness, Energy, Cost, Response Time) side-by-side 
              for each algorithm. The chart uses different scales (left Y-axis for Fitness, right Y-axis for other metrics) 
              to enable comprehensive comparison. <strong> This visualization helps identify algorithms that excel in multiple 
              objectives or find trade-offs between different performance criteria.</strong> Use this chart for holistic algorithm 
              evaluation and multi-objective optimization analysis.
            </Typography>
          </Alert>
          {!hasData || multiMetricData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={multiMetricData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" label={{ value: 'Fitness', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" label={{ value: 'Energy, Cost, Response Time', angle: 90, position: 'insideRight' }} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="Fitness" fill={chartColors.ga} name="Fitness" />
                <Area yAxisId="right" type="monotone" dataKey="Energy" fill={chartColors.culture} fillOpacity={0.6} name="Energy (Joules)" />
                <Area yAxisId="right" type="monotone" dataKey="Cost" fill={chartColors.frog} fillOpacity={0.6} name="Cost" />
                <Line yAxisId="right" type="monotone" dataKey="ResponseTime" stroke={chartColors.sflaga} strokeWidth={2} name="Response Time (ms)" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Convergence Speed Chart */}
      <TabPanel value={value} index={10}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Convergence Speed Analysis
          </Typography>
          <Alert 
            icon={<InfoIcon fontSize="inherit" />} 
            severity="info" 
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" component="span">
              This chart analyzes the convergence speed of each algorithm through two metrics: Improvement Rate (fitness gain per iteration, scaled) 
              and Iterations to 90% Performance (number of iterations needed to reach 90% of final improvement). 
              <strong> Higher improvement rates and fewer iterations indicate faster convergence,</strong> which is crucial for 
              time-constrained optimization problems. This analysis helps identify algorithms that balance convergence speed 
              with solution quality, valuable for real-world applications where computation time is limited.
            </Typography>
          </Alert>
          {!hasData || convergenceSpeedData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available. Please run optimization to view charts.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={convergenceSpeedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" label={{ value: 'Improvement Rate (scaled)', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" label={{ value: 'Iterations to 90%', angle: 90, position: 'insideRight' }} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="improvementRate" fill={chartColors.ga} name="Improvement Rate (×1000)" />
                <Line yAxisId="right" type="monotone" dataKey="iterationsTo90Percent" stroke={chartColors.sflaga} strokeWidth={3} name="Iterations to 90% Performance" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default ChartsView;
